import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import DashStatCard from '../../components/ui/DashStatCard'
import StatusBadge from '../../components/ui/StatusBadge'
import DashAvatar from '../../components/ui/DashAvatar'
import DashIcon from '../../components/ui/DashIcon'
import ReassignCourierModal from '../../components/admin/ReassignCourierModal'
import { useDeliveries } from '../../hooks/useDeliveries'
import { analyticsAPI } from '../../api/analytics'
import type { Delivery } from '../../types'

// ─── Status chip config ───────────────────────────────────────────────────────

const STATUS_CHIPS = [
  { key: '',                                               label: 'Tous' },
  { key: 'pending,validated,matching',                     label: "En attente d'assign" },
  { key: 'in_transit,picked_up,accepted,assigned',         label: 'En route' },
  { key: 'failed,unreachable',                             label: 'SLA risque' },
  { key: 'returned',                                       label: 'Retour' },
  { key: 'cancelled',                                      label: 'Annulés' },
]

// ─── Helper ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short' })

const truncate = (s?: string, n = 20) =>
  s && s.length > n ? s.slice(0, n) + '…' : (s ?? '—')

// ─── Row ──────────────────────────────────────────────────────────────────────

function DeliveryRow({ d, onReassign }: { d: Delivery; onReassign: (d: Delivery) => void }) {
  const navigate = useNavigate()
  const expediteurName = (d.expediteur as unknown as { name?: string; company?: { name?: string } } | undefined)?.company?.name
    ?? (d.expediteur as unknown as { name?: string } | undefined)?.name
    ?? '—'

  const livreurName = (d.livreur as unknown as { name?: string } | undefined)?.name

  const from = d.pickup?.address ?? ''
  const to = d.dropoff?.address ?? ''
  const fromCity = from.split(',').pop()?.trim() ?? from.slice(0, 15)
  const toCity = to.split(',').pop()?.trim() ?? to.slice(0, 15)

  return (
    <tr>
      <td>
        <span className="row-id">{d.trackingCode}</span>
      </td>
      <td>
        <div className="cell-user">
          <DashAvatar name={expediteurName} size={26} />
          <b style={{ fontSize: '.84rem' }}>{truncate(expediteurName, 18)}</b>
        </div>
      </td>
      <td>
        <b style={{ color: 'var(--navy-900)', fontSize: '.85rem' }}>{d.dropoff?.contactName ?? '—'}</b>
        <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{d.dropoff?.contactPhone}</div>
      </td>
      <td>
        {livreurName
          ? (
            <div className="cell-user">
              <DashAvatar name={livreurName} size={24} />
              <span style={{ fontSize: '.82rem' }}>{livreurName.split(' ')[0]}</span>
            </div>
          )
          : <span style={{ color: 'var(--amber)', fontSize: '.82rem', fontWeight: 600 }}>À assigner</span>
        }
      </td>
      <td>
        <div className="cell-route">
          <span className="from">{truncate(fromCity, 14)}</span>
          <span className="to">→ {truncate(toCity, 14)}</span>
        </div>
      </td>
      <td><StatusBadge status={d.status} /></td>
      <td className="mono">{d.price?.base} TND</td>
      <td className="mono" style={{ color: 'var(--muted)' }}>{fmtDate(d.createdAt)}</td>
      <td className="col-actions">
        <button onClick={() => navigate(`/admin/livraisons/${d._id}`)}>Voir</button>
        <button onClick={() => onReassign(d)}>Ré-assigner</button>
      </td>
    </tr>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminDeliveries() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [reassignTarget, setReassignTarget] = useState<Delivery | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1) }, 500)
    return () => clearTimeout(t)
  }, [search])

  const { data: statsData } = useQuery({
    queryKey: ['admin-stats-month'],
    queryFn: () => analyticsAPI.getGlobalStats('month'),
    staleTime: 2 * 60 * 1000,
  })

  const { data, isLoading, isFetching } = useDeliveries({
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
    page,
    limit: 20,
  })

  const deliveries: Delivery[] = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  // Stat card values
  const todayTotal   = statsData?.deliveries.total ?? 0
  const waiting      = statsData?.deliveries.pending ?? 0
  const enRoute      = statsData?.deliveries.inTransit ?? 0
  const slaRisque    = statsData?.deliveries.failed ?? 0

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Tous les colis</h1>
          <p>Recherche, supervision et intervention sur l'ensemble des commandes.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Export CSV</button>
          <button className="btn btn-primary"><DashIcon name="zap" size={14} /> Ré-assigner en masse</button>
        </div>
      </div>

      <div className="stat-grid stat-grid-4" style={{ marginBottom: 18 }}>
        <DashStatCard
          label="Total (30 jours)"
          value={todayTotal.toLocaleString('fr-TN')}
          trend={`Taux succès : ${statsData?.deliveries.successRate ?? 0}%`}
          icon="package"
        />
        <DashStatCard
          label="En attente d'assign"
          value={waiting.toLocaleString('fr-TN')}
          trend={waiting > 0 ? 'Action requise' : 'Aucun en attente'}
          icon="clock"
          accent="dark"
        />
        <DashStatCard
          label="En route"
          value={enRoute.toLocaleString('fr-TN')}
          trend={`Annulés : ${statsData?.deliveries.cancelled ?? 0}`}
          icon="truck"
        />
        <DashStatCard
          label="Échecs récents"
          value={slaRisque.toLocaleString('fr-TN')}
          trend={slaRisque > 0 ? 'Vérification requise' : 'Aucun échec'}
          icon="alert"
          accent={slaRisque > 0 ? 'green' : undefined}
        />
      </div>

      <div className="card" style={{ marginBottom: 14, padding: 14 }}>
        <div className="chip-row">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.key}
              className={`chip${statusFilter === c.key ? ' is-active' : ''}`}
              onClick={() => { setStatusFilter(c.key); setPage(1) }}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <div className="tbl-search">
            <DashIcon name="search" size={14} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ID colis, expéditeur, destinataire, téléphone, adresse..."
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)', marginLeft: 'auto', marginRight: 12 }}>
            {isFetching && !isLoading ? '⟳ Actualisation…' : `${total.toLocaleString('fr-TN')} colis`}
          </div>
        </div>

        <div className="tbl-scroll">
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              Chargement…
            </div>
          ) : deliveries.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
              <DashIcon name="package" size={28} />
              <p style={{ marginTop: 8 }}>
                {debouncedSearch ? `Aucun résultat pour "${debouncedSearch}"` : 'Aucun colis trouvé'}
              </p>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Expéditeur</th>
                  <th>Destinataire</th>
                  <th>Livreur</th>
                  <th>Trajet</th>
                  <th>Statut</th>
                  <th>COD</th>
                  <th>Créé</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => <DeliveryRow key={d._id} d={d} onReassign={setReassignTarget} />)}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Précédent
            </button>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Page {page} / {totalPages}</span>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Suivant →
            </button>
          </div>
        )}
      </div>

      <ReassignCourierModal
        parcel={reassignTarget}
        isOpen={!!reassignTarget}
        onClose={() => setReassignTarget(null)}
        onReassigned={(livreur) => {
          toast.success(`Colis ${reassignTarget?.trackingCode} assigné à ${livreur.name}`)
          queryClient.invalidateQueries({ queryKey: ['deliveries'] })
          setReassignTarget(null)
        }}
      />
    </>
  )
}
